import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  fromDatetimeLocalValue,
  sheinDateTimeToLocalValue,
} from '@/shein-platform/utils/sheinDateRange';

const WEEKDAY_LABELS = ['一', '二', '三', '四', '五', '六', '日'];
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

function pad2(n) {
  return String(n).padStart(2, '0');
}

function parseSheinParts(value) {
  const text = String(value || '').trim();
  const dateOnlyMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    return {
      date: new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3])),
      hour: 0,
      minute: 0,
      second: 0,
    };
  }
  // 兼容 ISO / SHEIN：yyyy-MM-dd[ T]HH:mm[:ss]
  const full = text.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (full) {
    return {
      date: new Date(Number(full[1]), Number(full[2]) - 1, Number(full[3])),
      hour: Number(full[4]) || 0,
      minute: Number(full[5]) || 0,
      second: Number(full[6]) || 0,
    };
  }
  const local = sheinDateTimeToLocalValue(value);
  if (!local) return null;
  const [datePart, timePart = '00:00'] = local.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm, ss] = timePart.split(':').map(Number);
  if (!y || !m || !d) return null;
  return {
    date: new Date(y, m - 1, d),
    hour: Number.isFinite(hh) ? hh : 0,
    minute: Number.isFinite(mm) ? mm : 0,
    second: Number.isFinite(ss) ? ss : 0,
  };
}

function buildCalendarDays(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  let startOffset = first.getDay();
  startOffset = startOffset === 0 ? 6 : startOffset - 1;

  const days = [];
  const prevMonthLast = new Date(year, month, 0).getDate();
  for (let i = startOffset - 1; i >= 0; i -= 1) {
    days.push({ date: new Date(year, month - 1, prevMonthLast - i), outside: true });
  }
  for (let day = 1; day <= last.getDate(); day += 1) {
    days.push({ date: new Date(year, month, day), outside: false });
  }
  let nextDay = 1;
  while (days.length % 7 !== 0 || days.length < 35) {
    days.push({ date: new Date(year, month + 1, nextDay), outside: true });
    nextDay += 1;
  }
  return days;
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isDayBefore(day, boundary) {
  if (!day || !boundary) return false;
  return startOfDay(day).getTime() < startOfDay(boundary).getTime();
}

function buildDraftDateTime(date, hour, minute, second) {
  if (!date) return null;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    Number(hour) || 0,
    Number(minute) || 0,
    Number(second) || 0,
    0,
  );
}

function toSheinValue(date, hour, minute, second = 0, withSeconds = false) {
  if (!date) return '';
  const time = withSeconds
    ? `${pad2(hour)}:${pad2(minute)}:${pad2(second)}`
    : `${pad2(hour)}:${pad2(minute)}`;
  const local = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${time}`;
  return fromDatetimeLocalValue(local);
}

function toSheinDateValue(date) {
  if (!date) return '';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function formatDisplay(value, dateOnly = false, withSeconds = false) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (dateOnly) {
    const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : text;
  }
  const parts = parseSheinParts(text);
  if (!parts?.date) return text.replace('T', ' ');
  const d = parts.date;
  const base = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(parts.hour)}:${pad2(parts.minute)}`;
  return withSeconds ? `${base}:${pad2(parts.second)}` : base;
}

/**
 * 日期时间下拉选择：点击 input 弹出日历 + 时分（可选秒），确认后回写 SHEIN 时间串。
 * @param {boolean} [dateOnly] 仅选日期（不含时分），值为 yyyy-MM-dd
 * @param {boolean} [withSeconds] 展示并回写秒（yyyy-MM-dd HH:mm:ss）
 * @param {boolean} [disablePast] 禁用当前时刻之前的日期/时间
 */
export default function SheinDateTimePicker({
  value = '',
  onChange,
  placeholder = fixedUi("请选择日期时间"),
  disabled = false,
  allowClear = true,
  className = '',
  ariaLabel = '选择日期时间',
  dateOnly = false,
  withSeconds = false,
  disablePast = false,
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState({});
  const [nowMs, setNowMs] = useState(() => Date.now());

  const parsed = useMemo(() => parseSheinParts(value), [value]);
  const [viewYear, setViewYear] = useState(() => parsed?.date.getFullYear() ?? new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => parsed?.date.getMonth() ?? new Date().getMonth());
  const [draftDate, setDraftDate] = useState(() => parsed?.date || null);
  const [draftHour, setDraftHour] = useState(() => pad2(parsed?.hour ?? 0));
  const [draftMinute, setDraftMinute] = useState(() => pad2(parsed?.minute ?? 0));
  const [draftSecond, setDraftSecond] = useState(() => pad2(parsed?.second ?? 0));

  const calendarDays = useMemo(
    () => buildCalendarDays(viewYear, viewMonth),
    [viewYear, viewMonth],
  );

  const now = useMemo(() => new Date(nowMs), [nowMs]);
  const minNavMonth = useMemo(
    () => (disablePast ? new Date(now.getFullYear(), now.getMonth(), 1) : null),
    [disablePast, now],
  );

  const draftDateTime = useMemo(
    () => buildDraftDateTime(draftDate, draftHour, draftMinute, withSeconds ? draftSecond : 0),
    [draftDate, draftHour, draftMinute, draftSecond, withSeconds],
  );

  const draftIsPast = Boolean(
    disablePast
    && draftDateTime
    && (
      dateOnly
        ? isDayBefore(draftDateTime, now)
        : draftDateTime.getTime() <= nowMs
    ),
  );

  const canGoPrevYear = !minNavMonth || viewYear > minNavMonth.getFullYear();
  const canGoPrevMonth = !minNavMonth
    || new Date(viewYear, viewMonth - 1, 1).getTime() >= minNavMonth.getTime();

  useEffect(() => {
    if (!open || !disablePast) return undefined;
    setNowMs(Date.now());
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [open, disablePast]);

  useEffect(() => {
    if (!open || !disablePast || !draftDate || dateOnly) return;
    if (!isSameDay(draftDate, now)) return;
    // 选中「今天」时，若当前时分秒已过期，自动抬到下一秒，避免确认失败却看不出原因
    if (!draftDateTime || draftDateTime.getTime() > nowMs) return;
    const next = new Date(nowMs + 1000);
    setDraftHour(pad2(next.getHours()));
    setDraftMinute(pad2(next.getMinutes()));
    setDraftSecond(pad2(next.getSeconds()));
  }, [open, disablePast, dateOnly, draftDate, draftDateTime, now, nowMs]);

  const updatePanelPosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panelWidth = Math.max(rect.width, 320);
    const estimatedHeight = panelRef.current?.offsetHeight || (dateOnly ? 300 : withSeconds ? 400 : 360);
    const gap = 4;
    const margin = 8;
    let left = rect.left;
    if (left + panelWidth > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - panelWidth - margin);
    }
    const spaceBelow = window.innerHeight - rect.bottom - gap - margin;
    const top = estimatedHeight <= spaceBelow || spaceBelow >= rect.top
      ? rect.bottom + gap
      : Math.max(margin, rect.top - estimatedHeight - gap);
    setPanelStyle({
      position: 'fixed',
      top,
      left,
      width: panelWidth,
      zIndex: 13000,
    });
  }, [dateOnly, withSeconds]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePanelPosition();
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updatePanelPosition, viewYear, viewMonth]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inRoot && !inPanel) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const isDayDisabled = (date) => disablePast && isDayBefore(date, now);

  const isHourDisabled = (hour) => {
    if (!disablePast || !draftDate || dateOnly || !isSameDay(draftDate, now)) return false;
    return Number(hour) < now.getHours();
  };

  const isMinuteDisabled = (minute) => {
    if (!disablePast || !draftDate || dateOnly || !isSameDay(draftDate, now)) return false;
    if (Number(draftHour) > now.getHours()) return false;
    if (Number(draftHour) < now.getHours()) return true;
    return Number(minute) < now.getMinutes();
  };

  const isSecondDisabled = (second) => {
    if (!disablePast || !withSeconds || !draftDate || dateOnly || !isSameDay(draftDate, now)) return false;
    if (Number(draftHour) > now.getHours()) return false;
    if (Number(draftHour) < now.getHours()) return true;
    if (Number(draftMinute) > now.getMinutes()) return false;
    if (Number(draftMinute) < now.getMinutes()) return true;
    return Number(second) <= now.getSeconds();
  };

  const openPicker = () => {
    if (disabled) return;
    const next = parseSheinParts(value);
    let base = next?.date || new Date();
    let hour = next?.hour ?? 0;
    let minute = next?.minute ?? 0;
    let second = next?.second ?? 0;
    if (disablePast) {
      const nowDate = new Date();
      if (!next?.date || isDayBefore(base, nowDate)) {
        base = nowDate;
        hour = nowDate.getHours();
        minute = nowDate.getMinutes();
        second = Math.min(59, nowDate.getSeconds() + 1);
      } else if (!dateOnly) {
        const candidate = buildDraftDateTime(base, hour, minute, second);
        if (candidate && candidate.getTime() <= nowDate.getTime()) {
          const bump = new Date(nowDate.getTime() + 1000);
          base = bump;
          hour = bump.getHours();
          minute = bump.getMinutes();
          second = bump.getSeconds();
        }
      }
      setNowMs(nowDate.getTime());
    }
    setViewYear(base.getFullYear());
    setViewMonth(base.getMonth());
    setDraftDate(base);
    setDraftHour(pad2(hour));
    setDraftMinute(pad2(minute));
    setDraftSecond(pad2(second));
    setOpen(true);
  };

  const shiftMonth = (delta) => {
    const next = new Date(viewYear, viewMonth + delta, 1);
    if (minNavMonth && next.getTime() < minNavMonth.getTime()) return;
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  const shiftYear = (delta) => {
    const nextYear = viewYear + delta;
    if (minNavMonth) {
      if (nextYear < minNavMonth.getFullYear()) return;
      if (nextYear === minNavMonth.getFullYear() && viewMonth < minNavMonth.getMonth()) {
        setViewYear(nextYear);
        setViewMonth(minNavMonth.getMonth());
        return;
      }
    }
    setViewYear(nextYear);
  };

  const confirm = () => {
    if (!draftDate) return;
    if (disablePast) {
      if (dateOnly) {
        if (isDayBefore(draftDate, new Date())) return;
      } else {
        const candidate = buildDraftDateTime(
          draftDate,
          draftHour,
          draftMinute,
          withSeconds ? draftSecond : 0,
        );
        if (!candidate || candidate.getTime() <= Date.now()) return;
      }
    }
    if (dateOnly) {
      onChange?.(toSheinDateValue(draftDate));
    } else {
      onChange?.(toSheinValue(
        draftDate,
        Number(draftHour),
        Number(draftMinute),
        Number(draftSecond),
        withSeconds,
      ));
    }
    setOpen(false);
  };

  const pickDay = (date) => {
    if (isDayDisabled(date)) return;
    setDraftDate(date);
    if (!dateOnly) return;
    // 仅日期模式：点选即确认
    onChange?.(toSheinDateValue(date));
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange?.('');
    setOpen(false);
  };

  const panel = open && !disabled ? createPortal(
    <div
      ref={panelRef}
      className={`spo-datetime-picker__panel${dateOnly ? ' spo-datetime-picker__panel--date-only' : ''}`}
      style={panelStyle}
      role="dialog"
      aria-label={ariaLabel}
    >
      <div className="spo-datetime-picker__nav">
        <button
          type="button"
          className="spo-datetime-picker__nav-btn"
          onClick={() => shiftYear(-1)}
          disabled={!canGoPrevYear}
          aria-label={fixedUi("上一年")}
        >
          «
        </button>
        <button
          type="button"
          className="spo-datetime-picker__nav-btn"
          onClick={() => shiftMonth(-1)}
          disabled={!canGoPrevMonth}
          aria-label={fixedUi("上一月")}
        >
          ‹
        </button>
        <span className="spo-datetime-picker__current">{viewYear} 年 {pad2(viewMonth + 1)} 月</span>
        <button type="button" className="spo-datetime-picker__nav-btn" onClick={() => shiftMonth(1)} aria-label={fixedUi("下一月")}>›</button>
        <button type="button" className="spo-datetime-picker__nav-btn" onClick={() => shiftYear(1)} aria-label={fixedUi("下一年")}>»</button>
      </div>
      <div className="spo-datetime-picker__weekdays">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className="spo-datetime-picker__grid">
        {calendarDays.map((item) => {
          const active = isSameDay(item.date, draftDate);
          const dayDisabled = isDayDisabled(item.date);
          const key = `${item.date.getFullYear()}-${item.date.getMonth()}-${item.date.getDate()}`;
          return (
            <button
              key={key}
              type="button"
              disabled={dayDisabled}
              className={`spo-datetime-picker__day${item.outside ? ' is-outside' : ''}${active ? ' is-active' : ''}${dayDisabled ? ' is-disabled' : ''}`}
              onClick={() => pickDay(item.date)}
            >
              {item.date.getDate()}
            </button>
          );
        })}
      </div>
      {dateOnly ? null : (
        <div className="spo-datetime-picker__time">
          <label>
            <span>{fixedUi("时")}</span>
            <select value={draftHour} onChange={(e) => setDraftHour(e.target.value)}>
              {HOUR_OPTIONS.map((h) => (
                <option key={h} value={h} disabled={isHourDisabled(h)}>{h}</option>
              ))}
            </select>
          </label>
          <span className="spo-datetime-picker__time-sep">:</span>
          <label>
            <span>{fixedUi("分")}</span>
            <select value={draftMinute} onChange={(e) => setDraftMinute(e.target.value)}>
              {MINUTE_OPTIONS.map((m) => (
                <option key={m} value={m} disabled={isMinuteDisabled(m)}>{m}</option>
              ))}
            </select>
          </label>
          {withSeconds ? (
            <>
              <span className="spo-datetime-picker__time-sep">:</span>
              <label>
                <span>{fixedUi("秒")}</span>
                <select value={draftSecond} onChange={(e) => setDraftSecond(e.target.value)}>
                  {MINUTE_OPTIONS.map((s) => (
                    <option key={s} value={s} disabled={isSecondDisabled(s)}>{s}</option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
        </div>
      )}
      <div className="spo-datetime-picker__foot">
        {allowClear ? (
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={clear}>{fixedUi("清空")}</button>
        ) : <span />}
        <div className="spo-datetime-picker__foot-right">
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={() => setOpen(false)}>{fixedUi("取消")}</button>
          {dateOnly ? null : (
            <button
              type="button"
              className="spo-btn spo-btn--primary spo-btn--sm"
              disabled={!draftDate || draftIsPast}
              onClick={confirm}
            >
              {fixedUi("确定")}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div
      className={`spo-datetime-picker${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}${dateOnly ? ' spo-datetime-picker--date-only' : ''}${withSeconds ? ' spo-datetime-picker--with-seconds' : ''}${className ? ` ${className}` : ''}`}
      ref={rootRef}
      role="combobox"
      aria-expanded={open}
      aria-haspopup="dialog"
    >
      <input
        ref={inputRef}
        type="text"
        readOnly
        disabled={disabled}
        className="spo-input spo-datetime-picker__input"
        value={formatDisplay(value, dateOnly, withSeconds)}
        placeholder={placeholder}
        onClick={openPicker}
        onFocus={openPicker}
        aria-autocomplete="none"
      />
      <span className="spo-datetime-picker__icon" aria-hidden onClick={openPicker}>▾</span>
      {panel}
    </div>
  );
}
